'use client';

import { motion, type PanInfo } from 'framer-motion';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { cn } from '@/lib/utils';

interface CarouselProps {
  children: ReactNode[];
  className?: string;
  /** Number of visible cards at each breakpoint. */
  visibleCount?: { base?: number; sm?: number; md?: number; lg?: number };
  /** Show navigation arrows. Defaults to auto (arrows only when content overflows). */
  showArrows?: boolean;
}

/**
 * Horizontal carousel with optional prev/next arrows. Designed for event
 * cards where the container shows a fixed number of items and the user
 * can page through them.
 *
 * Uses framer-motion for smooth drag-to-swipe and animated transitions.
 */
export function Carousel({
  children,
  className,
  visibleCount = { base: 1, sm: 2, lg: 3 },
  showArrows,
}: CarouselProps) {
  const trackRef = useRef<HTMLDivElement>(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [maxIndex, setMaxIndex] = useState(0);
  const [resolvedVisible, setResolvedVisible] = useState(1);
  const [needsNav, setNeedsNav] = useState(false);

  const items = Array.isArray(children) ? children : [children];
  const totalItems = items.length;

  /** Calculate how many cards are visible at the current viewport width. */
  const computeVisible = useCallback(() => {
    const width = window.innerWidth;
    if (width >= 1024) return visibleCount.lg ?? 3;
    if (width >= 640) return visibleCount.sm ?? visibleCount.base ?? 2;
    return visibleCount.base ?? 1;
  }, [visibleCount]);

  /** Recalculate visible count and max index on resize. */
  useEffect(() => {
    const recalc = () => {
      const visible = computeVisible();
      setResolvedVisible(visible);
      setMaxIndex(Math.max(0, totalItems - visible));
      setNeedsNav(totalItems > visible);
    };

    recalc();
    window.addEventListener('resize', recalc);
    return () => window.removeEventListener('resize', recalc);
  }, [totalItems, computeVisible]);

  /** Clamp current index when maxIndex changes (e.g. after resize). */
  useEffect(() => {
    setCurrentIndex((prev) => Math.min(prev, maxIndex));
  }, [maxIndex]);

  const goTo = useCallback(
    (index: number) => setCurrentIndex(Math.max(0, Math.min(index, maxIndex))),
    [maxIndex]
  );

  const goPrev = useCallback(() => goTo(currentIndex - 1), [currentIndex, goTo]);
  const goNext = useCallback(() => goTo(currentIndex + 1), [currentIndex, goTo]);

  /** Handle drag/swipe gestures from framer-motion. */
  const handleDragEnd = useCallback(
    (_: unknown, info: PanInfo) => {
      const swipeThreshold = 50;
      if (info.offset.x > swipeThreshold) {
        goPrev();
      } else if (info.offset.x < -swipeThreshold) {
        goNext();
      }
    },
    [goPrev, goNext]
  );

  const shouldShowArrows = showArrows ?? needsNav;

  return (
    <div className={cn('relative', className)}>
      {/* Navigation arrows */}
      {shouldShowArrows && currentIndex > 0 && (
        <button
          onClick={goPrev}
          className="absolute left-0 top-1/2 z-10 -translate-x-4 -translate-y-1/2 rounded-full border border-neutral-200 bg-white p-2 shadow-md transition-colors hover:bg-neutral-50"
          aria-label="Sebelumnya"
        >
          <ChevronLeft className="h-5 w-5 text-neutral-700" />
        </button>
      )}

      {shouldShowArrows && currentIndex < maxIndex && (
        <button
          onClick={goNext}
          className="absolute right-0 top-1/2 z-10 -translate-y-1/2 translate-x-4 rounded-full border border-neutral-200 bg-white p-2 shadow-md transition-colors hover:bg-neutral-50"
          aria-label="Selanjutnya"
        >
          <ChevronRight className="h-5 w-5 text-neutral-700" />
        </button>
      )}

      {/* Track */}
      <div className="overflow-hidden">
        <motion.div
          ref={trackRef}
          className="flex gap-6"
          animate={{ x: `${-(currentIndex * (100 / resolvedVisible))}%` }}
          transition={{ type: 'spring', stiffness: 300, damping: 30 }}
          drag="x"
          dragConstraints={{
            left: 0,
            right: 0,
          }}
          dragElastic={0.1}
          onDragEnd={handleDragEnd}
          style={{ width: `${(totalItems / resolvedVisible) * 100}%` }}
        >
          {items.map((child, i) => (
            <div
              key={i}
              className="shrink-0"
              style={{ width: `${100 / totalItems}%` }}
            >
              {child}
            </div>
          ))}
        </motion.div>
      </div>
    </div>
  );
}
